// Derived "show the subscribe step?" predicate for tier-aware onboarding
// (PE-1643, Phase 2b). The MIRROR of preFormInviteComplete's
// isPreFormInviteDurableComplete: a PURE function of server state, never a
// stored step or a client flag — so a returning user re-evaluates it from
// GET /merchant on ANY device (full resume, property 5).
//
// THE FIELD CONTRACT (the subtlest thing in this phase — see PE-1643):
//   - INTENT: flat `merchant.onboardingSelectedPlan` — the tier the org picked
//     on the marketing site, emitted by accounts-backend's
//     flatten_v2_to_v1_compat (PR #108). This FE is its first consumer.
//   - CURRENT PLAN: nested `merchant.billingDetails.plan` — the entitlement tier
//     (the field MarketingPaywall.tsx / LegacyBilling.js / EntitlementsContext
//     already read). The webhook flips THIS on payment.
//   - NEVER read flat `merchant.billingPlan` — that maps to
//     billingDetails.processingFees.tier (the donor FEE tier, a different §6
//     axis). There is no flat entitlement-`plan` field.
//
// Only BASE and PLUS are self-serve paid tiers onboarded through this step.
// STARTER (free) / absent → no step. PRO / ENTERPRISE are gated out of self-
// serve onboarding (handled by accounts-backend's launch-tier gate) → no step.

// The tiers this onboarding step subscribes. Kept in lockstep with
// accounts-backend ONBOARDING_SELF_SERVE_TIERS = {BASE, PLUS} (NOT is_paid_plan,
// which includes PRO). PRO/ENTERPRISE never reach the subscribe step.
const SELF_SERVE_SUBSCRIBE_TIERS = ['BASE', 'PLUS'];

// Normalize a raw plan string to an uppercase tier, or null if absent/garbage.
// Matches MarketingPaywall.readPlan / LegacyBilling normalization (uppercase).
function normalizePlan(raw) {
  if (typeof raw !== 'string') return null;
  const upper = raw.trim().toUpperCase();
  return upper === '' ? null : upper;
}

// The org's PAID-TIER INTENT from the flat onboardingSelectedPlan field, or null.
// Returns a tier only when it is one we self-serve onboard (BASE/PLUS); any other
// value (STARTER, PRO, ENTERPRISE, garbage, absent) → null (no subscribe step).
export function getOnboardingSubscribeIntent(merchant) {
  const intent = normalizePlan(merchant?.onboardingSelectedPlan);
  if (intent === null) return null;
  return SELF_SERVE_SUBSCRIBE_TIERS.includes(intent) ? intent : null;
}

// The org's CURRENT entitlement plan from nested billingDetails.plan, normalized
// upper, or null. This is what the webhook flips on a completed payment.
export function getCurrentPlan(merchant) {
  return normalizePlan(merchant?.billingDetails?.plan);
}

/** Paid signup checkout is required independently of Stripe Connect or tours.
 * Completed onboarding may retain historical signup intent after later plan changes.
 */
export function shouldShowSubscribeStep(merchant) {
  const intent = getOnboardingSubscribeIntent(merchant);
  if (!intent) return false;
  // A server-recorded cancellation belongs to an existing subscriber returning
  // to Starter. Tutorial progress is never evidence of subscription completion.
  if (merchant?.billingDetails?.subscriptionStatus === 'canceled') return false;
  const currentPlan = getCurrentPlan(merchant);
  // Active subscription history survives legitimate tier changes. An unpaid
  // Starter signup (even with a completed tutorial) is not subscription history.
  const confirmedPaidSubscription = ['BASE', 'PLUS', 'PRO', 'ENTERPRISE'].includes(currentPlan)
    && ['active', 'trialing', 'past_due', 'unpaid', 'paused'].includes(merchant?.billingDetails?.subscriptionStatus);
  return !confirmedPaidSubscription && currentPlan !== intent;
}
